import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AuthenticationService } from '@core/authentication.service';
import { CacheService } from '@core/cache.service';
import { environment } from '@environments/environment';
import { ApiResponse } from '@models/api-response';
import { IClassroom } from '@models/classroom';
import { IClassroomProductDescription } from '@models/classroom-products';
import { KeyValuePair } from '@models/key-value-pair';
import { ILessonAssetLink } from '@models/lesson-asset-link';
import { LessonAssetLinkLaunchError, LessonAssetLinkLaunchResult } from '@models/lesson-asset-link-launch-result';
import { ILicense } from '@models/license/license';
import { LicenseAssignee } from '@models/license/license-assignee';
import { LicenseValid } from '@models/license/license-valid';
import { SchoolLicense } from '@models/license/school-license';
import { MultiAssetsTaskResponse, PrintMultiAssetsRequest } from '@models/print-multi-assets.model';
import { IProduct, Product, ProductSku } from '@models/product';
import { IProductComponent } from '@models/product-component';
import { ProductLicense } from '@models/product-license';
import { IProductLine } from '@models/product-line';
import { IProductVariant } from '@models/product-variant';
import { QuickLesson } from '@models/quick-lesson';
import { QuickLessonData } from '@models/quick-lesson-data';
import { School } from '@models/school';
import { ISpellingWord } from '@models/spelling-word';
import { StudentRank } from '@models/student-rank';
import {
  NavigationPagedItemResponse,
  NavigationPagedResponse,
  NavigationResponse,
  Topic,
  TopicGroup
} from '@models/topic';
import { TypeResponseMessage } from '@models/type-response-message';
import { IViewerAsset, IViewerAssetJson, MaterialGroup, ViewerAsset } from '@models/viewer-asset';
import {
  StatefulService
} from '@services/stateful-service-management/stateful-service-interface/stateful-service-interface';
import { LICENSE_TYPES } from '@shared/constants/license-constants';
import { Capabilities } from '@shared/enums/capabilities';
import { CapabilityType } from '@shared/enums/capability-type';
import { GradeType } from '@shared/enums/grade-type';
import { LaunchType } from '@shared/enums/launch-type';
import { LicenseFilterType } from '@shared/enums/license-filter-type';
import { LicenseAssignmentType } from '@shared/enums/license-status';
import { ProductKey } from '@shared/enums/product-key';
import { ProductType } from '@shared/enums/product-type';
import { SpellingLevelType } from '@shared/enums/spelling-level-type';
import { StateType } from '@shared/enums/state-type';
import { TopicType } from '@shared/enums/topic-type';
import { VariantType } from '@shared/enums/variant-type';
import { ViewerAssetType, ViewerLanguage } from '@shared/enums/viewer-asset';
import { Helpers } from '@shared/helpers';
import { copyArray, copyObject } from '@shared/zb-object-helper/object-helper';
import { instantiateApiResponseFromArray } from '@shared/zb-rxjs-operators/rxjs-operators';
import { ActivityItem, QuestData } from '@students/program/program.model';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, map, skipWhile } from 'rxjs/operators';
import { AppRoutingService } from './app-routing.service';
import { AppConfigService } from './appconfig.service';
import { UserService } from './user.service';

/** Contains State and API calls. */
@Injectable({
  providedIn: 'root',
})
export class ProductService implements StatefulService {
  /**
   * All unexpired product licenses across all product lines
   */
  activeProductLicenses: ProductLicense[] = [];

  licensesLoaded: BehaviorSubject<boolean>;
  currentProductLines$ = new BehaviorSubject<IProductLine[]>(null);
  currentVariantType$ = new BehaviorSubject<VariantType>(null);
  currentProductLineKey$ = new BehaviorSubject<string>(null);

  showClassesAndReports = false;

  readonly handwritingProducts: ProductType[] = [
    ProductType.hw2020n,
    ProductType.hw2025n,
    ProductType.hw2020tx,
    ProductType.hw2020txs,
    ProductType.laesc2020n,
    ProductType.laesc2020tx,
    ProductType.laesc2020txs,
  ];

  readonly spellingProducts: ProductType[] = [
    ProductType.spcn2022,
    ProductType.spcn2020tx,
    ProductType.spcn2020txs,
    ProductType.spcn2016n,
  ];

  readonly superkidsProducts: ProductType[] = [
    ProductType.hea2011,
    ProductType.sk2015,
    ProductType.sk2017,
    ProductType.sk2026,
    ProductType.fsk2021,
  ];

  readonly gumProducts: ProductType[] = [
    ProductType.gum2021n,
  ];

  /**
   * Product component launch types that appear on the Materials page by default.
   */
  readonly materialLaunchTypes = [
    LaunchType.DailyRoutine,
    LaunchType.SuperKidsOnlineFun,
    LaunchType.SuperkidsOwnWebPages,
    LaunchType.SuperSmart,
    LaunchType.Viewer,
    LaunchType.DirectViewer,
    LaunchType.WhiteboardActivity,
    LaunchType.NumberPractice,
    LaunchType.MatchingWithZaney,
    LaunchType.WorksheetMaker,
    LaunchType.eBook2026,
    LaunchType.GumLessons,
    LaunchType.AssessmentPreview,
  ];

  readonly eBook2026TopicTypes: readonly TopicType[] = [
    TopicType.Reader,
    TopicType.Magazine,
    TopicType.Book
  ] as const;

  readonly studentViewerTopicTypes: readonly TopicType[] = [
    TopicType.Video,
    TopicType.SuperSmart,
    TopicType.MaterialViewer
  ] as const;

  readonly launchInPortalLaunchTypes: readonly LaunchType[] = [
    LaunchType.Viewer,
    LaunchType.DirectViewer,
    LaunchType.PrintAllResources
  ] as const;

  readonly genericLockedReason: string = `<p>These units have been locked by your teacher.</p>
    <p>You can unlock these units by first completing the units available to you.</p>`;

  readonly specialCaseProductKeys = [ProductKey.ms2024.toString()];

  /** Material assets types that are able to be printed from the Materials page. */
  readonly printableAssetTypes: ViewerAssetType[] = [ViewerAssetType.Pdf, ViewerAssetType.ImagePrint];

  constructor(
    private http: HttpClient,
    private appConfig: AppConfigService,
    private cache: CacheService,
    private authService: AuthenticationService,
    private userService: UserService,
    private appRoutingService: AppRoutingService,
  ) {
    this.licensesLoaded = new BehaviorSubject<boolean>(false);
  }

  private getContentManifestUrl(manifestFile: string): string {
    return `${this.appConfig.assetUrl}content/${manifestFile}`;
  }

  private getQuickLessonsUrl(productType: string, variantKey: string): string {
    return `${this.appConfig.apiUrl}/digital-resources/teacher/handwriting/quick-lessons/product/${productType}/grade/${variantKey}`;
  }

  private getTopicsApiUrl(productType: ProductType, grade: string, classroomId: string): string {
    const query = `?grade=${grade}&productType=${productType}`;
    return `${this.appConfig.apiUrl}/digital-resources/navigation/classroom/${classroomId}${query}`;
  }

  private getLeaderboardUrl(classroomId: string, productType: ProductType): string {
    return `${this.appConfig.apiUrl}/student/leaderboard/${classroomId}/product/${productType}`;
  }

  private getProductsUrl(): string {
    return `${this.appConfig.apiUrl}/product/navigation`;
  }

  // @todo Api should not use superkids-specific content service for viewer asset.
  private getViewerAssetsUrl(
    productType: ProductType, variantType: VariantType | string,
    type: string = 'viewer-asset', classroom: IClassroom = null
  ): string {
    const url = `${this.appConfig.apiUrl}/digital-resources/superkids/${type}/product/${productType}/grade/${variantType}`;

    if (classroom) {
      // @todo Optionally add the classroom id in case the Api needs it later.
      return `${url}?classroomId=${classroom.classroomId}`;
    }

    if (productType === ProductType.ece2024) {
      return `${this.appConfig.apiUrl}/digital-resources/${type}/product/${productType}/grade/${variantType}`;
    }
    return url;
  }

  /**
   * Checks for the current user's license for the assetId and classroom (optional)
   * @param assetId
   * @param classroom
   */
  determineLicenseValid(assetId: string, classroom?: IClassroom): Observable<ApiResponse<LicenseValid[]>> {
    let url = `${this.appConfig.apiUrl}/digital-resources/license/viewer-asset/asset/${assetId}`;

    if (classroom) {
      url += `/classroom/${classroom.classroomId}`;
    }

    return this.http.get<ApiResponse<LicenseValid[]>>(url)
      .pipe(
        map(res => new ApiResponse<LicenseValid[]>(true, {
          response: res.response.map(v => copyObject(v, LicenseValid)),
          messages: res.messages,
        })),
      );
  }

  updateUserSchoolLicenses(
    schoolId: string, userId: string, assignments: LicenseAssignee[], assignmentType: LicenseAssignmentType = null
  ): Observable<ApiResponse<SchoolLicense[]>> {
    const params: KeyValuePair[] = [{ key: 'userId', value: userId }];
    if (assignmentType) {
      params.push({ key: 'assignmentType', value: assignmentType });
    }
    const url = Helpers.buildUrlWithParameters(this.appConfig.apiUrl, `/educational-unit/${schoolId}/license`, params);
    return this.http.patch<ApiResponse<ILicense[]>>(url, assignments)
      .pipe(
        map(res => new ApiResponse<SchoolLicense[]>(true, {
          response: res.response.map(v => copyObject(v, SchoolLicense)),
          messages: res.messages,
        })),
      );
  }

  updateUserSchoolLicense(
    schoolId: string, userId: string, skuId: string, isAssigned: boolean
  ): Observable<ApiResponse<SchoolLicense[]>> {
    const assignments: LicenseAssignee[] = [{ skuId, isAssigned, assignedId: userId }];
    return this.updateUserSchoolLicenses(schoolId, userId, assignments);
  }

  getAvailableUserSchoolLicenses(schoolId: string, userId: string): Observable<ApiResponse<SchoolLicense[]>> {
    const params: KeyValuePair[] = [
      { key: 'userId', value: userId },
      { key: 'licenseFilterType', value: LicenseFilterType.ShowUnassigned },
    ];
    const url = Helpers.buildUrlWithParameters(this.appConfig.apiUrl, `/educational-unit/${schoolId}/license`, params);
    return this.http.get<ApiResponse<ILicense[]>>(url)
      .pipe(
        map(res => new ApiResponse<SchoolLicense[]>(true, {
          response: res.response.map(v => copyObject(v, SchoolLicense)),
          messages: [],
        })),
      );
  }


  /**
   * Maps all available licenses for a user across a set of schools by school identifier.
   *
   * TODO replace this implementation with one that does not make multiple API calls.
   */
  getUserSchoolLicenses(schools: School[], userId: string): Observable<KeyValuePair<SchoolLicense[]>[]> {
    const obs = schools.map((school) => {
      const url = `${this.appConfig.apiUrl}/educational-unit/${school.schoolId}/license?userId=${userId}`;
      return this.http.get<ApiResponse<ILicense[]>>(url)
        .pipe(
          map(res => (
            { key: school.schoolId, value: res.response.map(v => copyObject(v, SchoolLicense)) })),
        );
    });
    // TODO if we do have to make multiple API calls, then consider forkJoin instead
    return combineLatest(obs);
  }

  /**
   * Returns the list of all licenses at a district.
   */
  getUserDistrictLicenses(districtId: string, request: {
    userId: string,
    licenseFilterType?: LicenseFilterType,
  }): Observable<ApiResponse<SchoolLicense[]>> {
    const url = `${this.appConfig.apiUrl}/educational-unit/${districtId}/license`;
    const params = Helpers.objToQueryParams({ ...request, isDistrict: true });
    return this.http.get<ApiResponse<ILicense[]>>(url, { params })
      .pipe(instantiateApiResponseFromArray(SchoolLicense));
  }

  getProducts(): Observable<ApiResponse<IProductLine[]>> {
    return this.http.get(this.getProductsUrl())
      .pipe(
        map((res: ApiResponse<IProductLine[]>) => new ApiResponse<IProductLine[]>(true, {
          response: res.response.map((pl) => {
            const variants = pl.variants.map((variant) => {
              const skus = variant.skus.map(sku => new ProductSku(sku));
              return { ...variant, skus };
            });
            return { ...pl, variants };
          }),
          messages: res.messages,
        })),
      );
  }

  getProductsWithTeacherLicenses(productLines: IProductLine[]): IProductLine[] {
    let productLineFiltered: IProductLine;
    const productLinesFiltered: IProductLine[] = [];
    productLines.forEach((productLine) => {
      const productType = Helpers.getProductTypeFromProductLineId(productLine.productLineKey);
      const isNotSuperKids = Helpers.supplementalProductTypes.includes(productType);
      let variantFiltered: IProductVariant;
      const variantsFiltered: IProductVariant[] = [];
      productLine.variants.forEach((variant) => {
        const skus = variant.skus
          .filter(sku => sku.licenseAssignmentType === LicenseAssignmentType.Teacher
            || (sku.licenseAssignmentType === LicenseAssignmentType.Classroom && isNotSuperKids));
        if (skus.length > 0) {
          variantFiltered = cloneDeep(variant);
          variantFiltered.skus = skus;
          variantsFiltered.push(variantFiltered);
        }
      });
      if (variantsFiltered.length > 0) {
        productLineFiltered = cloneDeep(productLine);
        productLineFiltered.variants = variantsFiltered;
        productLinesFiltered.push(productLineFiltered);
      }
    });
    return productLinesFiltered;
  }

  /**
   * Gets the Viewer asset list for a particular grade based on the path provided.
   */
  getContentManifest(basePath: string): Observable<TypeResponseMessage<Array<IViewerAssetJson>>> {
    return this.http.get(this.getContentManifestUrl(basePath)).pipe(
      map((response: any) => new TypeResponseMessage<Array<IViewerAssetJson>>(true, [], response)),
      catchError(error => (
        of(new TypeResponseMessage<Array<IViewerAssetJson>>(false, [error], []))
      )));
  }

  /**
   * Gets the "Quick Lessons" unit/lesson list for a particular grade based on the path provided.
   */
  getQuickLessons(
    productType: string, variantKey: string, language: ViewerLanguage, edition: string
  ): Observable<QuickLesson[]> {
    return this.getQuickLessonsFromApi(productType, variantKey).pipe(
      map((response) => {
        const assets = response.filter((m: QuickLessonData) => (
          language === m.language && (m.edition === 'any' || edition === m.edition)
        ));
        return assets.reduce((result: QuickLesson[], asset: QuickLessonData): QuickLesson[] => {
          const index = result.findIndex(l => l.lesson === asset.lesson && l.unit === asset.unit);
          if (index === -1) {
            result.push({
              unit: asset.unit,
              unit_name: asset.unit_name,
              lesson: asset.lesson,
              lesson_name: asset.lesson_name,
              language: asset.language,
              assets: [asset.id],
              edition,
            } as QuickLesson);
          } else {
            result[index].assets.push(asset.id);
          }
          return result.sort((a: QuickLesson, b: QuickLesson) => {
            let ret = 0;
            if (a.lesson < b.lesson) {
              ret = -1;
            } else if (a.lesson > b.lesson) {
              ret = 1;
            }
            return ret;
          });
        }, []);
      }));
  }

  getQuickLessonsFromApi(productType: string, grade: string): Observable<QuickLessonData[]> {
    return this.http.get(this.getQuickLessonsUrl(productType, grade))
      .pipe(
        map((response: ApiResponse<QuickLessonData[]>) => copyArray(response.response, QuickLessonData)),
        map(data => data.map(item => copyObject({
          ...item,
          language: item.language === ViewerLanguage.en ? ViewerLanguage.English : ViewerLanguage.Spanish,
        }, QuickLessonData)))
      );
  }

  /** Gets the spelling word list for a topic key, productType and level. */
  getSpellingWordList(topic: Topic, productType: ProductType): Observable<ApiResponse<ISpellingWord[]>> {
    const levelType = topic.productMetadata && topic.productMetadata.wordListLevel
      ? topic.productMetadata.wordListLevel
      : SpellingLevelType.OnLevel;
    return this.getSpellingWordListDirect(topic.topicKey, levelType, productType);
  }

  getSpellingWordListDirect(
    topicKey: string, levelType: string, productType: ProductType
  ): Observable<ApiResponse<ISpellingWord[]>> {
    const url = `${this.appConfig.apiUrl}/digital-resources/spelling/word-list/${topicKey}`;
    const params = new HttpParams({
      fromObject: { levelType, productType },
    });
    return this.http.get(url, { params })
      .pipe(
        map(res => new ApiResponse<ISpellingWord[]>(true, res)),
      );
  }

  /**
   * Get the product path based on product component or asset.
   */
  getProductLaunchPathByComponentAsset(
    productLineKey: string, productComponent: IProductComponent, activity: string = null,
    asset: IViewerAsset = null, variantType: string = null
  ): string {
    let path: string;
    let params: KeyValuePair[] = [
      { key: 'componentId', value: productComponent.componentId },
    ];
    if (productComponent.launchType === LaunchType.Viewer
      || productComponent.launchType === LaunchType.DirectViewer) {
      if (asset.viewerAssetType === ViewerAssetType.Download) {
        // Direct download.
        path = this.getDownloadableViewerAssetUrl(productLineKey, productComponent.componentId, asset, variantType);
      } else {
        const series = this.getViewerSeries(productLineKey);
        let { language } = asset;
        if (series === 'ms2024') {
          // Handles special case for Mindscapes.
          const assetArray = asset.assetId.split('_');
          const languageIndicator = assetArray[1];
          language = this.getLanguageFromSeries(series, languageIndicator);
        }

        params.push({ key: 'activity', value: activity });
        params.push({ key: 'assetId', value: asset.assetId });
        params.push({ key: 'language', value: language });
        path = this.getProductLaunchPath(productLineKey, productComponent, params);
      }
    } else if (productComponent.launchType === LaunchType.eBook) {
      params.push({ key: 'activity', value: 'ebook' });
      path = this.getProductLaunchPath(productLineKey, productComponent, params);
    } else if (productComponent.launchType === LaunchType.NumberPractice) {
      params.push({ key: 'activity', value: 'number-practice' });
      path = this.getProductLaunchPath(productLineKey, productComponent, params);
    } else if (productComponent.launchType === LaunchType.eBook2026) {
      params = [{ key: 'book', value: asset.assetId }];
      path = this.getProductLaunchPath(productLineKey, productComponent, params);
    } else {
      params.push({ key: 'activity', value: activity });
      path = this.getProductLaunchPath(productLineKey, productComponent, params);
    }
    return path;
  }

  getStudentActivityLaunchPath(
    classroomId: string,
    productLineKey: string,
    component: IProductComponent,
    activity: ActivityItem,
    quest?: QuestData
  ): string {
    const params: KeyValuePair[] = [];
    // is undefined if the given topic type is not in the enum
    const topicType = TopicType[activity.topicType];
    if (!topicType) {
      console.warn('Invalid topic type found while generating Activity URL', activity.topicType);
    }
    if (this.isEbook2026Activity(component, topicType)) {
      // ebook 2026 uses book query param for topic key
      // and does not have any of the other standard digital resource query params
      if (quest) {
        params.push({ key: 'book', value: quest.questId });
      } else {
        params.push({ key: 'book', value: activity.assetId });
      }
      if (activity.tableOfContentId) {
        params.push({ key: 'toc', value: activity.tableOfContentId });
      }
    } else if (this.isViewerActivity(component, topicType)) {
      params.push({ key: 'classroomId', value: classroomId });
      params.push({ key: 'componentId', value: component.componentId });
      params.push({ key: 'activity', value: 'viewer' });
      if (quest) {
        params.push({ key: 'topicKey', value: quest.questKey });
        params.push({ key: 'assetId', value: quest.questId });
      } else {
        params.push({ key: 'topicKey', value: activity.topicKey });
        params.push({ key: 'assetId', value: activity.assetId });

      }
    } else {
      params.push(
        { key: 'componentId', value: component.componentId },
        { key: 'classroomId', value: classroomId ?? '' },
      );
      if (Helpers.vendorTopicTypes.includes(topicType)) {
        // "vendor" topic types do not end up routing to digital-resources, they are separate filament apps
        // this means they need the token provided directly, in order to communicate with the API
        params.push({
          key: 'token',
          value: this.authService.apiToken,
        });
      }
      if (activity.productMetadata?.dominantHand) {
        params.push({
          key: 'dominantHand',
          value: activity.productMetadata?.dominantHand
        });
      }
      if (activity.productMetadata?.wordListLevel) {
        params.push({
          key: 'level',
          value: activity.productMetadata?.wordListLevel
        });
      }
      if (quest) {
        params.push({ key: 'activity', value: quest.questKey });
      } else {
        params.push({ key: 'activity', value: activity.topicKey });
      }
    }
    return this.getProductLaunchPath(
      productLineKey,
      component,
      params,
      topicType,
    );
  }

  getDigitalResourcesBaseUrl(replaceOriginPort: boolean = true): string {
    const origin = this.appConfig.getOrigin(replaceOriginPort);
    return `${origin}/sites/digital-resources/`;
  }

  /**
   * Get the product path based on product and optional parameters.
   */
  getProductLaunchPath(
    productLineKey: string,
    component: IProductComponent,
    srcParams: KeyValuePair[] = [],
    topicType: TopicType = null
  ): string {
    const isViewerActivity = this.isViewerActivity(component, topicType);
    // To open Viewer Asset in DR, set isViewerLaunchType to false and remove the argument from the getOrigin call
    const isLaunchInPortalType = this.launchInPortalLaunchTypes.includes(component.launchType)
      || isViewerActivity;
    // Do not replace the port for local URLs when launchType is Viewer.
    // Remove the `!isViewerLaunchType` argument from getOrigin to open Viewer Asset in DR
    const origin = this.appConfig.getOrigin(!isLaunchInPortalType);
    let parameters: KeyValuePair[] = [...srcParams];
    let basePath = this.getDigitalResourcesBaseUrl(!isLaunchInPortalType);
    const isEbook2026Activity = this.isEbook2026Activity(component, topicType);

    // Viewer assets are routed to the Viewer Module of this repo.
    if (isLaunchInPortalType && (component.launchType === LaunchType.Viewer
      || component.launchType === LaunchType.DirectViewer)) {
      basePath = this.getViewerActivityBasePath();
    } else if (component.launchType === LaunchType.PrintAllResources) {
      const variant = srcParams.find(param => param.key === 'variant')?.value as VariantType;
      const componentId = srcParams.find(param => param.key === 'componentId')?.value;
      basePath = `${origin}${this.appRoutingService.getTeachersPrintAssetsRoute(productLineKey, variant, componentId)}/`;
    }

    if (Helpers.vendorTopicTypes.includes(topicType)) {
      // Filament Activity
      // Removes componentId and adds parameters to help Filament.
      basePath = `${this.appConfig.assetUrl}sites/handwriting-trace/index.html`;
      const grade = this.getGradeDesignationFromComponentId(component.componentId);
      const productType = this.getProductTypeFromProductLine(productLineKey);

      parameters = parameters.filter(param => param.key !== 'componentId');
      parameters.push({ key: 'grade', value: Helpers.mapGradeToGradeType(grade) });
      parameters.push({ key: 'productType', value: productType });
      parameters.push({ key: 'apiUrl', value: `${this.appConfig.apiUrl}/` });
    }

    if (component.launchType === LaunchType.SuperKidsOnlineFun
      && topicType !== TopicType.Assessment) {
      // Non-Assessment Superkids Activities
      if (isViewerActivity) {
        // SK2026 Resource Viewer Activities
        basePath = this.getViewerActivityBasePath();
      } else if (isEbook2026Activity) {
        // SK2026 Reader Activities
        basePath = this.getEbook2026BasePath();
      } else {
        // SK2017 SKOF App
        basePath = `${environment.skofUrl}/sites/skof/app/index.html`;
        if (!this.appConfig.isProduction) {
          parameters.push({
            key: 'apiendpoint',
            value: `${environment.proxyApiUrl === ''
              ? environment.apiUrl
              : environment.proxyApiUrl}/superkids`
          });
        }
      }
    } else if (isEbook2026Activity) {
      basePath = this.getEbook2026BasePath();
    }

    // send correct productType for texas products
    if (topicType === TopicType.Lesson && productLineKey.includes(StateType.TX)) {
      const productType = this.getProductTypeFromProductLine(productLineKey);
      parameters.push({ key: 'productType', value: productType });
    }

    // If localhost testing, add the token to the query string so the
    // Digital-Resources request can be authenticated.
    if (!this.appConfig.isProduction
      && this.appConfig.checkForInternalUrl()
      && !isEbook2026Activity
      && !parameters.find(v => v.key === 'token')
      && !isLaunchInPortalType) {
      parameters.push({ key: 'token', value: this.authService.apiToken });
    }

    return this.buildUrl(basePath, parameters);
  }

  /**
   * Get the product path for a lesson material based on the product variant, component and optional parameters.
   */
  getLessonMaterialProductLaunchPath(
    productLineKey: string,
    productVariant: IProductVariant,
    componentId: string,
    activity: string,
    extraParams: KeyValuePair[],
    isDRLaunchType: boolean = false,
    asset: IViewerAsset = null,
  ): string {
    const params: KeyValuePair[] = [
      { key: 'componentId', value: componentId },
      { key: 'activity', value: activity },
    ].concat(extraParams);
    let product = this.findVariantProductComponent(productVariant, componentId);
    if (product) {
      let modifiedComponent: IProductComponent;
      if (isDRLaunchType) {
        modifiedComponent = {
          ...product,
          launchType: LaunchType.DailyRoutine,
        };
      } else {
        modifiedComponent = product;
      }
      return this.getProductLaunchPath(productLineKey, modifiedComponent, params);
    }
    if (asset) {
      // If we can't find the product by componentId, try to find with asset's componentId
      product = this.findVariantProductComponent(productVariant, asset.componentId);
      if (product && product.launchType === LaunchType.eBook2026) {
        const ebookParams: KeyValuePair[] = [
          { key: 'book', value: asset ? asset.assetId : componentId }
        ].concat(extraParams);
        return this.getProductLaunchPath(productLineKey, product, ebookParams);
      }
      return null;
    }
    return null;
  }

  /**
   * Get the product launch path for a lesson asset link, or an error the caller handles. Does not toast.
   */
  getLessonAssetLinkProductLaunchPath(
    link: ILessonAssetLink,
    getTopicKey: () => string,
    assets: IViewerAsset[],
    productLineKey: string,
    productVariant: IProductVariant,
    currentTabName: string,
  ): LessonAssetLinkLaunchResult {
    let path = null;
    if (link.assetLinkType && link.assetId) {
      const matches = link.assetId.match(/^([a-zA-Z0-9-_+. ]+)(\/[a-zA-Z0-9-_+. ]+)?$/);
      if (matches) {
        const assetId = matches[1];
        const extraParams: KeyValuePair[] = [];
        const scheme = link.assetLinkType.toLocaleLowerCase();
        extraParams.push({ key: 'topicKey', value: getTopicKey() });
        extraParams.push({ key: 'navigatedFrom', value: 'lesson' });
        const asset = assets.find(a => a.assetId === assetId);
        if (scheme === 'asset') {
          if (asset) {
            if (asset.viewerAssetType === ViewerAssetType.Dr) {
              path = this.getLessonMaterialProductLaunchPath(
                productLineKey, productVariant, asset.componentId, asset.assetId, extraParams, true
              );
            } else {
              if (currentTabName) {
                extraParams.push({ key: 'lessonTab', value: currentTabName });
              }
              if (productLineKey) {
                extraParams.push({ key: 'productLineKey', value: productLineKey });
              }
              extraParams.push({ key: 'assetId', value: asset.assetId });
              extraParams.push({ key: 'language', value: asset.language });
              path = this.getLessonMaterialProductLaunchPath(
                productLineKey, productVariant, asset.componentId, 'viewer', extraParams
              );
            }
          } else {
            return { error: LessonAssetLinkLaunchError.MaterialNotFound };
          }
        } else if (scheme === 'ebook') {
          const page = (matches.length > 2 && matches[2]) ? matches[2].substring(1) : null;
          if (page) {
            extraParams.push({ key: 'page', value: page });
          }
          path = this.getLessonMaterialProductLaunchPath(
            productLineKey, productVariant, assetId, 'ebook', extraParams, false, asset
          );
        } else if (scheme === 'app') {
          // Launches directly with component Id so we don't have to do an asynchronous operation.
          const activity = matches.length > 2 && !!matches[2] ? matches[2].replace('/', '') : '';
          path = this.getLessonMaterialProductLaunchPath(
            productLineKey, productVariant, assetId, activity, extraParams
          );
        }
      }
    }

    if (path) {
      return { path };
    }
    return { error: LessonAssetLinkLaunchError.ProductNotFound };
  }

  private buildUrl(basePath: string, parameters: KeyValuePair<any>[]): string {
    return parameters.reduce((result, param, index) => {
      const { key, value } = param;
      const encoded = encodeURIComponent(value);
      if (index === 0) {
        return `${result}?${key}=${encoded}`;
      }
      return `${result}&${key}=${encoded}`;
    }, basePath);
  }

  private isEbook2026Activity(component: IProductComponent, topicType: TopicType = null): boolean {
    return component.launchType === LaunchType.eBook2026
      || (component.launchType === LaunchType.SuperKidsOnlineFun
        && this.eBook2026TopicTypes.includes(topicType));
  }

  getEbook2026BasePath() {
    return `${this.appConfig.getOrigin(false)}/ereader`;
  }

  private isViewerActivity(component: IProductComponent, topicType: TopicType = null): boolean {
    return component.launchType === LaunchType.Viewer
      || component.launchType === LaunchType.DirectViewer
      || (component.launchType === LaunchType.SuperKidsOnlineFun
        && this.studentViewerTopicTypes.includes(topicType));
  }

  private getViewerActivityBasePath() {
    return `${this.appConfig.getOrigin(false)}${this.appConfig.viewerModuleUrl}/`;
  }

  private findVariantProductComponent(productVariant: IProductVariant, componentId: string): IProductComponent {
    return productVariant.skus.reduce((ret: IProductComponent, sku) => {
      if (!ret) {
        return sku.components.find(c => c.componentId === componentId);
      }
      return ret;
    }, null);
  }

  /**
   * Get the product path based on product line, launchType and optional parameters.
   */
  getProductLaunchPathByProductLineLaunchTypeAndParams(
    productLineKey: string, launchType: LaunchType, srcParams: KeyValuePair[] = []
  ): string {
    const productComponent: IProductComponent = {
      productId: '',
      componentId: '',
      name: '',
      description: '',
      weight: 0,
      launchType,
      isActive: true,
      isTrial: false,
      isBaseProduct: true,
      filterOptions: [],
      isPinned: false,
      roles: [],
      subsection: null,
      subsectionWeight: null,
      materialGroups: [],
    };

    return this.getProductLaunchPath(productLineKey, productComponent, srcParams);
  }

  /**
   * Use this to get the ViewerLanguage.
   */
  getViewerLanguageFromSeries(series: string): ViewerLanguage {
    return series.substring(0, 2) === 'la'
      ? ViewerLanguage.Spanish
      : ViewerLanguage.English;
  }

  /**
   * This should have only be used for legacy Handwriting content that uses legacy "en" and "es" codes.
   *
   * @deprecated
   */
  getLanguageFromSeries(series: string, languageIndicator: string): ViewerLanguage {
    if (this.specialCaseProductKeys.indexOf(series) !== -1) {
      return languageIndicator.toLocaleLowerCase().includes(ViewerLanguage.es) ? ViewerLanguage.es : ViewerLanguage.en;
    }
    return series.substring(0, 2) === 'la'
      ? ViewerLanguage.es
      : ViewerLanguage.en;
  }

  /** Extracts language code from a book ID. */
  getLanguageFromBookId(product: Product): string {
    return product.bookid.includes('_es_') ? ViewerLanguage.es : ViewerLanguage.en;
  }

  /**
   * Normalizes viewer language return from API.
   */
  getNormalizedLanguage(language: ViewerLanguage): ViewerLanguage {
    if (language === ViewerLanguage.English) {
      return ViewerLanguage.en;
    }
    if (language === ViewerLanguage.Spanish) {
      return ViewerLanguage.es;
    }
    return language;
  }

  getEditionFromProductLineId(productLineId: string): string {
    return productLineId.toLocaleLowerCase().includes('tx') ? 'tx' : 'ntl';
  }

  /** Extracts national or texas edition from a book ID. */
  getEdition(product: Product): string {
    return product.bookid.includes('_tx_') ? 'tx' : 'ntl';
  }

  getViewerSeries(productLineId: string): string {
    // The series prefix is no longer consistently lowercase and can be something like LaEsc, HW, or JIW.
    return productLineId.replace(/^([a-zA-Z]{2,}[0-9]{4}(?:al(?![a-zA-Z]))?).*$/i, '$1').toLocaleLowerCase();
  }

  /** Gets the 202x series designation for a product handwriting/spelling */
  getViewerSeriesDesignation(viewerSeries: string): string {
    if (viewerSeries.slice(0, 5) === 'laesc') {
      return `hw${viewerSeries.slice(-4)}`;
    }
    if (viewerSeries.slice(0, 5) === 'wordh') {
      return `wh${viewerSeries.slice(-4)}`;
    }
    if (viewerSeries.slice(0, 3) === 'jiw') {
      return `jump${viewerSeries.slice(-4)}`;
    }
    return viewerSeries;
  }

  getViewerEngineFromComponentId(productLineId: string, componentId: string): string {
    const productType = this.getProductTypeFromProductLine(productLineId);
    const engineMatches = componentId.match(/_(G[PK0-9-]{1,}[MCLION]{0,1})_([A-Z-]+(2[MC])?)$/i);

    if (engineMatches && engineMatches.length > 2) {
      const engine = engineMatches[2].toLocaleLowerCase();

      if (productType === ProductType.jiw2021n) {
        const suffix = engineMatches[1].toLocaleLowerCase().slice(-1);
        if (suffix === 'i') {
          return `info_${engine}`;
        }
        if (suffix === 'o') {
          return `opin_${engine}`;
        }
        if (suffix === 'n') {
          return `narr_${engine}`;
        }
      }
      // for Catholic Practice Master need to add grade suffix to engine for second grade
      if (engine.includes('catholicpm') && engineMatches[1].includes('2')) {
        return `${engine}${engineMatches[1].toLocaleLowerCase().substring(1, 3)}`;
      }
      return engine;
    }
    return null;
  }

  /** Gets the thumbnail location from the product and viewer asset. Use this for supplemental products */
  getViewerThumbnail(asset: IViewerAsset, productLineId: string, componentId: string, variantType: string): string {
    const productLine = this.getViewerSeriesDesignation(this.getViewerSeries(productLineId));
    const gradeDesignation = this.getGradeDesignationFromComponentId(componentId);
    const variantDir = this.getVariantDir(productLine, variantType, gradeDesignation);
    const langCode = (asset.language === ViewerLanguage.English || asset.language === ViewerLanguage.en)
      ? ViewerLanguage.en : ViewerLanguage.es;
    const languagePath = productLine.includes('hw') ? `${langCode}/` : '';
    return this.getContentManifestUrl(`${variantDir.toLocaleLowerCase()}/${Helpers.getProductLineFolder(productLine)}/viewer/thumbs/${languagePath}${asset.assetId}.png`);
  }

  /** Gets the thumbnail for component materials.
   * Use this for Lesson/Material page setups i.e. Superkids etc.*/
  getMaterialThumbnail(component: IProductComponent): string {
    return `${this.appConfig.assetUrl}${component?.product?.baseAssetPath}${component?.product?.materialsThumbnailPath}${component.componentId}.png`;
  }

  /**
   * Creates a TaskGenerationId for multi-assets generation to use in {@link ProductService.getMultiAssetsBlobByTaskId}.
   * @param {PrintMultiAssetsRequest} body - contains assetIds and product information.
   * @returns {Observable<ApiResponse<MultiAssetsTaskResponse>>} Observable including TaskGenerationId.
   */
  getMultiAssetsTask(body: PrintMultiAssetsRequest): Observable<ApiResponse<MultiAssetsTaskResponse>> {
    const url: string = `${this.appConfig.apiUrl}/export/materials`;
    return this.http.post(url, body)
      .pipe(
        map(res => new ApiResponse<MultiAssetsTaskResponse>(true, { response: res })),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          return of(new ApiResponse<MultiAssetsTaskResponse>(false, err.error));
        })
      );
  }

  /**
   * Gets a task using a taskGenerationId created by {@link ProductService.getMultiAssetsTask}
   *
   * API returns 202 when task is still processing.
   * API returns 200 when task is complete.
   *
   * @param {string} taskGenerationId - the identifier for the multi-assets generation task.
   * @returns {Observable<ApiResponse<Blob>>} Observable including Blob URL of generated assets.
   */
  getMultiAssetsBlobByTaskId(taskGenerationId: string): Observable<ApiResponse<Blob>> {
    const url: string = `${this.appConfig.apiUrl}/export/materials/taskGeneration/${taskGenerationId}`;
    return this.http.get(url, { responseType: 'blob' })
      .pipe(
        skipWhile(loaded => !loaded),
        map((res: Blob) => {
          if (res.size > 0) {
            const blobUrl = URL.createObjectURL(res);

            return new ApiResponse<Blob>(true, { response: blobUrl });
          }
          // returns success, but without a response means the task is still processing, return empty Response object
          return new ApiResponse<Blob>(true);

        }),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          return of(new ApiResponse<Blob>(false, err.error));
        })
      );
  }

  /**
   * Gets the thumbnail for a material's asset.
   */
  getAssetThumbnail(component: IProductComponent, asset: IViewerAsset): string {
    return `${this.appConfig.assetUrl}${component?.product?.baseAssetPath}${component?.product?.materialsThumbnailPath}${asset.thumbnail}`;
  }

  getVariantDir(productLineName: string, variantType: string, gradeDesignation: string): string {
    return productLineName.includes('hw') ? `grade${gradeDesignation}` : variantType;
  }

  // Gets grade designation is from componentId, which is the only consistent method to get it.
  getGradeDesignationFromComponentId(componentId: string): string {
    const matches = componentId.match(/G([0-9K]+).?/i);
    if (matches && matches.length > 1) {
      return matches[1];
    }
    return 'none';
  }

  getMaterialGroupsFromApi(productType: ProductType, variantType: VariantType): Observable<ApiResponse<string[]>> {
    const url = `${this.appConfig.apiUrl}/digital-resources/material-group/product/${productType}/variant/${variantType}`;
    return this.http.get<ApiResponse<MaterialGroup[]>>(url)
      .pipe(
        map(res => new ApiResponse<string[]>(true, {
          response: res.response.map(group => group.materialGroup)
        }
        )));
  }

  saveMaterialGroupSelection(
    productType: ProductType, variantType: VariantType, materialGroup: MaterialGroup
  ): Observable<any> {
    const url = `${this.appConfig.apiUrl}/digital-resources/material-group/product/${productType}/variant/${variantType}`;
    const body = { materialGroupName: materialGroup.materialGroup };
    return this.http.patch<ApiResponse<MaterialGroup[]>>(url, body);
  }

  /**
   * Gets the viewer assets either from Api or legacy JSON.
   */
  getViewerAssets(
    productType: ProductType,
    variantType: VariantType | string,
    componentId: string = null,
    type: string = 'viewer-asset',
    classroom: IClassroom = null,
  ): Observable<ApiResponse<IViewerAsset[]>> {
    return this.getViewerAssetsFromApi(productType, variantType, type, classroom, componentId);
  }

  /**
   * Gets the viewer assets from the API.
   */
  getViewerAssetsFromApi(productType: ProductType, variantType: VariantType | string, type: string = 'viewer-asset',
    classroom: IClassroom = null, componentId: string = null): Observable<ApiResponse<IViewerAsset[]>> {
    return this.http.get<ApiResponse<IViewerAsset[]>>(
      this.getViewerAssetsUrl(productType, variantType, type, classroom))
      .pipe(
        map(res => new ApiResponse<IViewerAsset[]>(true, {
          // Supplemental, non-Material pages, split assets by product.
          response: (componentId ? res.response.filter(a => a.componentId === componentId)
            .map(item => ({
              ...item,
              language: this.getNormalizedLanguage(item.language),
            })) : [...res.response]),
          messages: [...res.messages],
        })),
      );
  }

  /**
   * Gets the absolute URL of a downloadable asset.
   */
  getDownloadableViewerAssetUrl(
    productLineKey: string, productComponentId: string, asset: IViewerAsset, variantType: string = null
  ): string {
    const series = this.getViewerSeries(productLineKey);
    const gradeDesignation = this.getGradeDesignationFromComponentId(productComponentId);
    const variantDir = this.getVariantDir(productLineKey, variantType, gradeDesignation);

    return `${this.appConfig.assetUrl}content/${variantDir.toLocaleLowerCase()}/${series}/viewer/downloads/${asset.fileName}`;
  }

  getTopicsByProductAndGrade(
    productType: ProductType, grade: string, classroomId: string, isLongPoll: boolean = false
  ): Observable<TopicGroup[]> {
    return this.getTopicsFromApi(productType, grade, classroomId, isLongPoll)
      .pipe(
        // Map topic groups by unit instead of topic ordinal for Handwriting.
        map((topicGroups: TopicGroup[]) => (this.isHandwriting(productType)
          ? this.mapTopicGroupsByUnit(topicGroups)
          : topicGroups
        ))
      );
  }

  getTopicsByProductAndGradePaging(
    productType: ProductType, grade: string, classroomId: string, selectedUnit?: number
  ): Observable<TopicGroup[]> {
    return this.getTopicsFromPagingApi(productType, grade, classroomId, selectedUnit)
      .pipe(
        // Map topic groups by unit instead of topic ordinal for Handwriting.
        map((topicGroups: TopicGroup[]) => (this.isHandwriting(productType)
          ? this.mapTopicGroupsByUnit(topicGroups)
          : topicGroups
        ))
      );
  }

  mapTopicGroupsByUnit(topicGroups: TopicGroup[]): TopicGroup[] {
    const combined: TopicGroup[] = [];
    topicGroups.forEach((topicGroup) => {
      const index = combined.findIndex(tg => tg.items[0].unit === topicGroup.items[0].unit);
      if (index === -1) {
        combined.push(topicGroup);
      } else {
        // Combines the topicGroup into the previous one.
        combined[index].items = combined[index].items.concat(topicGroup.items);
      }
    });
    return combined;
  }

  mapTopicGroupValues(group: TopicGroup): TopicGroup {
    const items = group.items || group.lessons;
    // Sets topic group isLocked when an item (the first) is locked since topics are locked by unit, not topic.
    const lockedItem = items.find(t => t.isLocked === true && t.starEarned !== true);
    let unit = group.unit ?? null;
    if (unit == null) {
      const matches = items[0].topicKey.match(/U(\d+)/);
      unit = (matches && matches.length > 1) ? Number.parseInt(matches[1], 10) : null;
    }
    return {
      unit,
      unitWeight: group.unitWeight ?? null,
      unitName: group.unitName || null,
      topicName: group.topicName || null,
      firstTopicKeySuffix: items[0]?.topicKeySuffix || null,
      grade: GradeType[group.grade] || null,
      isLocked: lockedItem ? lockedItem.isLocked : false,
      items: items.map((topic) => {
        const ret = {
          ...topic,
          unit,
          unitName: topic.unitName || group.unitName,
          unitWeight: topic.unitWeight || group.unitWeight,
        };
        if (topic.isLocked) {
          ret.lockedReason = topic.lockedReason ? topic.lockedReason : this.genericLockedReason;
        } else {
          ret.lockedReason = null;
        }

        return ret;
      }),
    };
  }

  mapTopicGroupPagedValues(group: NavigationResponse, pagedItems: NavigationPagedItemResponse[]): TopicGroup {
    const { items } = group;
    // Sets topic group isLocked when an item (the first) is locked since topics are locked by unit, not topic.
    const lockedItem = items.find(t => t.isLocked === true && t.starEarned !== true);
    let unit = group.unit ?? null;
    if (unit == null) {
      const matches = items[0].topicKey.match(/U(\d+)/);
      unit = (matches && matches.length > 1) ? Number.parseInt(matches[1], 10) : null;
    }

    return {
      unit,
      unitWeight: items[0]?.unitWeight ?? null,
      unitName: items[0]?.unitName || null,
      topicName: group.topicName || null,
      firstTopicKeySuffix: items[0]?.topicKeySuffix || null,
      grade: GradeType[items[0]?.grade] || null,
      isLocked: lockedItem ? lockedItem.isLocked : false,
      pagedItems: pagedItems ?? null,
      items: items.map((topic) => {
        const ret = {
          ...topic,
          unit,
          unitName: topic.unitName || items[0]?.unitName,
          unitWeight: topic.unitWeight || items[0]?.unitWeight,
        };
        if (topic.isLocked) {
          ret.lockedReason = topic.lockedReason ? topic.lockedReason : this.genericLockedReason;
        } else {
          ret.lockedReason = null;
        }

        return ret;
      }),
    };
  }

  private getTopicsFromApi(
    productType: ProductType, grade: string, classroomId: string, isLongPoll: boolean = false
  ): Observable<TopicGroup[]> {
    // Set long polling header to turn off portal interceptor loading spinner
    const requestOptions = isLongPoll ? { headers: new HttpHeaders({ 'zbportal-long-polling': 'true' }) } : {};

    return this.http.get<ApiResponse<TopicGroup[]>>(
      this.getTopicsApiUrl(productType, grade, classroomId), requestOptions
    )
      .pipe(
        map((res: ApiResponse<TopicGroup[]>) => res.response.map((g: TopicGroup) => this.mapTopicGroupValues(g)))
      );
  }


  private getTopicsFromPagingApi(
    productType: ProductType, grade: string, classroomId: string, selectedUnit?: number
  ): Observable<TopicGroup[]> {
    const data = {
      selectedUnit
    };
    return this.http.patch<ApiResponse<NavigationPagedResponse>>(
      this.getTopicsApiUrl(productType, grade, classroomId), data
    )
      .pipe(
        map((res: ApiResponse<NavigationPagedResponse>) => (
          res.response.navigationItems.map(g => this.mapTopicGroupPagedValues(g, res.response.pagedItems))))
      );
  }

  getLeaderboardForClassroom(
    classroomId: string, productType: ProductType = ProductType.gum2021n
  ): Observable<ApiResponse<StudentRank[]>> {
    return this.http.get(this.getLeaderboardUrl(classroomId, productType))
      .pipe(
        map((res: ApiResponse<StudentRank[]>) => new ApiResponse<StudentRank[]>(true, res)),
      );
  }

  getProductUrlForStudentActivity(classroomId: string,
    activityId: string,
    topicKey: string,
    productType: ProductType,
    grade: GradeType | string,
    // These are both models for "licenses". Whoever did this originally should be held responsible.
    licenses: (IClassroomProductDescription | SchoolLicense)[]): Observable<string> {

    const license = licenses.find((l: IClassroomProductDescription | SchoolLicense) => {
      if (l instanceof IClassroomProductDescription) {
        return l.productType === productType
          && Helpers.mapVariantToGradeType(l.variantType) === grade;
      }
      if (l instanceof SchoolLicense) {
        return l.productType === productType
          && Helpers.mapVariantToGradeType(l.variant) === grade;
      }
      return null;
    });

    if (license || this.userService.isAuthorizedForStudentActivity()) {
      return of(this.getProductUrlForStudentActivityDirect(classroomId, activityId, topicKey, productType, grade));
    }

    return of(null);
  }

  getProductUrlForStudentActivityDirect(
    classroomId: string,
    activityId: string,
    topicKey: string,
    productType: ProductType,
    grade: GradeType | string,
  ): string {
    const componentId = this.getComponentIdFromProductType(productType, grade);
    const params = `componentId=${componentId}&classroomId=${classroomId}&activity=${topicKey}&activityId=${activityId}`;
    return `${this.getDigitalResourcesBaseUrl(true)}?${params}`;
  }

  getProductProfileKey(productType: ProductType): string {
    switch (productType) {
    case ProductType.hw2020n:
    case ProductType.hw2025n:
    case ProductType.hw2020tx:
    case ProductType.hw2020txs:
    case ProductType.hw2026tx:
    case ProductType.laesc2020n:
    case ProductType.laesc2020tx:
    case ProductType.laesc2020txs:
    case ProductType.laesc2026tx:
      return 'handwritingProfiles';
    case ProductType.gum2021n:
      return 'gumProfiles';
    case ProductType.spcn2016n:
    case ProductType.spcn2020tx:
    case ProductType.spcn2020txs:
    case ProductType.spcn2022:
      return 'spellingProfiles';
    case ProductType.sk2015:
    case ProductType.sk2017:
    case ProductType.sk2026:
    case ProductType.fsk2021:
      return 'superkidsProfiles';
    default:
      throw new Error('Invalid product type for product profile');
    }
  }

  /**
   * @deprecated use Capabilities instead
   */
  isHandwriting(productType: ProductType): boolean {
    return this.handwritingProducts.includes(productType);
  }

  /**
   * @deprecated use Capabilities instead
   */
  isSpelling(productType: ProductType): boolean {
    return this.spellingProducts.includes(productType);
  }

  /**
   * @deprecated use Capabilities instead
   */
  isSuperkids(productType: ProductType): boolean {
    return this.superkidsProducts.includes(productType);
  }

  /**
   * Checks whether a product component is considered a "Material" for Materials pages.
   *
   * @param {IProductComponent} productComponent The product component i.e. launchable thing.
   */
  isMaterialComponent(productComponent: IProductComponent): boolean {
    const launchTypes = [...this.materialLaunchTypes];

    // We can use componentId for the ProductLine check because they're consistent.
    if (Helpers.hasProductCapability(productComponent.product, Capabilities.MaterialsEbook, CapabilityType.Teacher)) {
      launchTypes.push(LaunchType.eBook);
    }

    return launchTypes.includes(productComponent.launchType);
  }

  isAssetPrintable(asset: ViewerAsset): boolean {
    return this.printableAssetTypes.includes(asset.viewerAssetType) && asset.visible;
  }

  getPrintableAssetIdsForCategory(
    assets: IViewerAsset[],
    categoryName: string,
  ): string[] {
    return (assets ?? [])
      .filter(asset => this.isAssetPrintable(asset))
      .filter(asset => !categoryName || asset.category === categoryName)
      .map(asset => asset.assetId);
  }

  /** Launch path to open PrintAllResources page for Teacher. */
  getPrintAssetsLaunchPath(
    componentId: string, language: string, productLineKey: string, productType: ProductType,
    variantType: VariantType, categoryName: string
  ): string {
    const params: KeyValuePair[] = [
      { key: 'componentId', value: componentId },
      { key: 'language', value: language },
      { key: 'product', value: productType },
      { key: 'variant', value: variantType },
      { key: 'isDirectDownload', value: false },
      { key: 'category', value: categoryName },
    ];

    return this.getProductLaunchPathByProductLineLaunchTypeAndParams(
      productLineKey,
      LaunchType.PrintAllResources,
      params
    );
  }

  getProductTypeFromProductLine(productKey: string): ProductType {
    const matches = productKey.match(/^([a-z]+[0-9]+)([a-z]{0,})/i);
    if (matches) {
      const productLineKey = matches[0].toLocaleLowerCase();
      return ProductType[productLineKey] ? ProductType[productLineKey] : ProductType.None;
    }
    return ProductType.None;
  }

  /**
   * Extracts the product line from the lesson card topic key.
   *
   * @param {string} cardTopicKey The lesson card topic key, which is NOT considered a ZBPortal {Topic}.
   *
   * @returns {string|null}
   */
  getProductLineFromLessonCardTopicKey(cardTopicKey: string): string {
    const matches = cardTopicKey.match(/^([a-z]+[0-9]+)([a-z]{0,})/i);
    if (matches) {
      return matches[0].toLocaleUpperCase();
    }
    return null;
  }

  private getComponentIdFromProductType(productType: ProductType, gradeType: GradeType | string): string {
    // This is the best the front-end can do to figure out how to launch a component from a quest because a quest is
    // not related to a product component.
    const productTypePrefix: string = Object.keys(ProductType).find(key => ProductType[key] === productType);
    const grade: string = Helpers.mapGradeTypeToGrade(gradeType);
    const suffix: string = this.getQuestComponentSuffix(productType);
    return `${productTypePrefix.toLocaleUpperCase()}_G${grade}_${suffix}`;
  }

  private getQuestComponentSuffix(productType: ProductType): string {
    switch (productType) {
    case ProductType.hw2020n:
    case ProductType.hw2025n:
    case ProductType.hw2020tx:
    case ProductType.hw2020txs:
    case ProductType.laesc2020n:
    case ProductType.laesc2020tx:
    case ProductType.laesc2020txs:
      return 'HQ';
    case ProductType.gum2021n:
      return 'GQ';
    case ProductType.spcn2020tx:
    case ProductType.spcn2020txs:
    case ProductType.spcn2022:
      return 'SQ';
    case ProductType.spcn2016n:
      return 'OG';
    case ProductType.fsk2021:
    case ProductType.sk2015:
    case ProductType.sk2017:
    case ProductType.sk2026:
      return 'SKOF';
    default:
      throw new Error('Invalid product type for quests');
    }
  }

  /**
   * Gets a product line from product lines by product line key.
   *
   * @param {IProductLine[]} productLines an array of one or more productLines from product navigation.
   * @param {string} productLineKey the product line key to extract the productLine from.
   *
   * @returns {IProductLine}
   */
  getProductLineByKey(productLines: IProductLine[], productLineKey: string): IProductLine {
    return productLines.find(p => p.productLineKey === productLineKey);
  }

  /**
   * Gets a product line variant by variant type.
   *
   * @param productLine product line to get variant from
   * @param variantType variant type needed from the product line
   * @returns {IProductVariant} variant of product line
   */
  getProductVariantByVariantType(productLine: IProductLine, variantType: string): IProductVariant {
    return productLine?.variants?.find((variant: IProductVariant) => variant.variantType === variantType);
  }

  /**
   * Gets a product variant from product lines.
   *
   * @param {IProductLine[]} productLines an array of one or more productLines from product navigation.
   * @param {string} productLineKey the product line key to extract the productLine from.
   * @param {string} variantType the variant type / key.
   *
   * @returns {IProductVariant}
   */
  getProductLineVariant(
    productLines: IProductLine[],
    productLineKey: string,
    variantType: string): IProductVariant {
    return this.getProductLineByKey(productLines, productLineKey).variants.find(v => v.variantType === variantType);
  }

  /**
   * Gets a product component inside productLines.
   *
   * @param {IProductLine[]} productLines an array of one or more productLines from product navigation.
   * @param {string} productLineKey the product line key to extract the productLine from.
   * @param {string} variantType the variant type / key.
   * @param {string} productId the product component product identifier.
   *
   * @returns {IProductComponent}
   */
  getProductComponentById(
    productLines: IProductLine[],
    productLineKey: string,
    variantType: string,
    productId: string): IProductComponent {
    return this.getProductLineVariant(productLines, productLineKey, variantType)
      .skus.reduce((component: IProductComponent, sku: IProduct): IProductComponent => (!component
        ? sku.components.find(c => c.productId === productId)
        : component),
      null);
  }

  /**
   * Gets a product component inside productLines.
   *
   * @todo this should use the ngrx store instead, but ECE.
   *
   * @param {IProductLine[]} productLines an array of one or more productLines from product navigation.
   * @param {string} productLineKey the product line key to extract the productLine from.
   * @param {string} variantType the variant type / key.
   * @param {string} componentId the product component identifier to search for inside a SKU.
   *
   * @returns {IProductComponent}
   */
  getProductComponentByComponentId(
    productLines: IProductLine[],
    productLineKey: string,
    variantType: string,
    componentId: string): IProductComponent {
    return this.getProductLineVariant(productLines, productLineKey, variantType)
      .skus.reduce((component: IProductComponent, sku: IProduct): IProductComponent => (!component
        ? sku.components.find(c => c.componentId === componentId)
        : component),
      null);
  }

  isTrialProduct(productVariant: IProductVariant): boolean {
    const teacherSku = productVariant.skus
      .find(sku => sku.licenseAssignmentType === LicenseAssignmentType.Teacher);
    return teacherSku?.licenseType === LICENSE_TYPES[LICENSE_TYPES.Trial];
  }

  private getMaterialUrl(): string {
    return `${this.appConfig.apiUrl}/digital-resources/material`;
  }

  /**
   * The `setIsPinned` and `setHasThumbnailLinks` are only used to set the property to true.
   * Setting the parameter to false will only tell the API not to update that setting.
   * If it is necessary to remove the pin or remove the thumbnail links flag then use the
   * `removeMaterialSettings` function
   */
  updateMaterialSettings(
    productType: ProductType,
    gradeType: GradeType | string,
    componentId: string,
    setIsPinned: boolean,
    setHasThumbnailLinks: boolean): Observable<ApiResponse<boolean>> {
    const data = { productType, gradeType, componentId, setIsPinned, setHasThumbnailLinks };
    return this.http.patch<ApiResponse<any>>(this.getMaterialUrl(), data)
      .pipe(
        map(res => new ApiResponse<boolean>(true, { response: true, messages: [...res.messages] })),
      );
  }

  removeMaterialSettings(
    productType: ProductType,
    gradeType: GradeType | string,
    componentId: string,
    setIsPinned: boolean,
    setHasThumbnailLinks: boolean): Observable<ApiResponse<boolean>> {
    const body = { productType, gradeType, componentId, setIsPinned, setHasThumbnailLinks };
    return this.http.request<ApiResponse<any>>('delete', this.getMaterialUrl(), { body })
      .pipe(
        map(res => new ApiResponse<boolean>(true, { response: false, messages: [...res.messages] })),
      );
  }

  /** Resets the stateful properties of this service. */
  reset(): void {
    this.activeProductLicenses = [];
    this.licensesLoaded.next(false);
    this.currentProductLines$.next(null);
    this.currentVariantType$.next(null);
    this.currentProductLineKey$.next(null);
    this.showClassesAndReports = false;
  }
}
